/**
 * Shared time-grid used by both WeekView (7 columns) and DayView (1 column).
 */
import React, { useRef, useEffect } from 'react';
import type { TaskFull, MeetingFull } from '@/redux/internal-services/workspaceApiExtensions';
import {
  isSameDay, dateKey, layoutMeetings,
  HOUR_PX, DAY_START, MEETING_COLORS, PRIORITY_COLOR, fmtTime,
} from './calendarUtils';

const HOURS = Array.from({ length: 18 }, (_, i) => i + DAY_START); // 6am–11pm
const GRID_HEIGHT = HOURS.length * HOUR_PX;

interface Props {
  days: Date[];
  tasks: TaskFull[];
  meetings: MeetingFull[];
  onTaskClick?: (taskId: string) => void;
  onMeetingClick?: (meetingId: string) => void;
}

const TimeGrid: React.FC<Props> = ({ days, tasks, meetings, onTaskClick, onMeetingClick }) => {
  const today = new Date();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Scroll to 8am on mount
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = (8 - DAY_START) * HOUR_PX - 16;
    }
  }, []);

  const nowPx = (() => {
    const h = today.getHours() - DAY_START;
    const m = today.getMinutes();
    return (h * 60 + m) / 60 * HOUR_PX;
  })();

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Day headers */}
      <div className="flex border-b border-ui-border flex-shrink-0">
        <div className="w-14 flex-shrink-0" />
        {days.map((day) => {
          const isToday = isSameDay(day, today);
          const dk = dateKey(day);
          const dayTasks = tasks.filter((t) => t.due_date && dateKey(new Date(t.due_date)) === dk);

          return (
            <div key={dk} className="flex-1 min-w-0 border-l border-ui-border">
              {/* Day label */}
              <div className={`py-2 text-center ${isToday ? 'text-accent-text' : 'text-ui-text3'}`}>
                <div className="text-micro font-semibold uppercase tracking-wider">
                  {day.toLocaleDateString(undefined, { weekday: 'short' })}
                </div>
                <div className={`text-lg font-bold leading-tight ${isToday ? 'text-accent-text' : 'text-ui-text'}`}>
                  {day.getDate()}
                </div>
              </div>

              {/* All-day task strip */}
              {dayTasks.length > 0 && (
                <div className="px-1 pb-1 flex flex-col gap-0.5 border-t border-ui-border">
                  {dayTasks.slice(0, 2).map((t) => {
                    const isOverdue = new Date(t.due_date!) < today && !['completed', 'cancelled'].includes(t.status);
                    const dot = PRIORITY_COLOR[t.priority] ?? PRIORITY_COLOR.normal;
                    return (
                      <button
                        key={t.task_id}
                        type="button"
                        onClick={() => onTaskClick?.(t.task_id)}
                        className="w-full text-left text-xs font-medium px-1 py-0.5 rounded truncate flex items-center gap-1 bg-ui-subtle border border-ui-border text-ui-text2 hover:border-accent/30"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isOverdue ? 'bg-danger' : dot}`} />
                        <span className="truncate">{t.title}</span>
                      </button>
                    );
                  })}
                  {dayTasks.length > 2 && (
                    <span className="text-xs text-ui-text3 px-1">+{dayTasks.length - 2} more</span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Scrollable time grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto min-h-0">
        <div className="flex" style={{ height: GRID_HEIGHT }}>
          {/* Hour labels */}
          <div className="w-14 flex-shrink-0 relative">
            {HOURS.map((h) => (
              <div
                key={h}
                className="absolute right-2 text-xs text-ui-text3 -translate-y-2"
                style={{ top: (h - DAY_START) * HOUR_PX }}
              >
                {h === 12 ? '12pm' : h < 12 ? `${h}am` : `${h - 12}pm`}
              </div>
            ))}
          </div>

          {/* Day columns */}
          {days.map((day) => {
            const isToday = isSameDay(day, today);
            const dk = dateKey(day);
            const dayMeetings = meetings.filter((m) => dateKey(new Date(m.start_time)) === dk);
            const positioned = layoutMeetings(dayMeetings);

            return (
              <div
                key={dk}
                className="flex-1 min-w-0 border-l border-ui-border relative"
                style={{ height: GRID_HEIGHT }}
              >
                {/* Hour lines */}
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className="absolute left-0 right-0 border-t border-ui-border"
                    style={{ top: (h - DAY_START) * HOUR_PX }}
                  />
                ))}

                {/* Half-hour lines */}
                {HOURS.map((h) => (
                  <div
                    key={`${h}h`}
                    className="absolute left-0 right-0 border-t border-ui-border/40"
                    style={{ top: (h - DAY_START) * HOUR_PX + HOUR_PX / 2 }}
                  />
                ))}

                {/* Current time indicator */}
                {isToday && nowPx >= 0 && nowPx <= GRID_HEIGHT && (
                  <div className="absolute left-0 right-0 z-20 flex items-center" style={{ top: nowPx }}>
                    <div className="w-2 h-2 rounded-full bg-danger -ml-1 flex-shrink-0" />
                    <div className="flex-1 h-px bg-danger" />
                  </div>
                )}

                {/* Meetings */}
                {positioned.map(({ meeting: m, col, cols, top, height }) => {
                  const c = MEETING_COLORS[m.meeting_type] ?? MEETING_COLORS.other;
                  const colW = 100 / cols;
                  return (
                    <button
                      key={m.meeting_id}
                      type="button"
                      onClick={() => onMeetingClick?.(m.meeting_id)}
                      className={`absolute rounded-lg px-1.5 py-1 text-left overflow-hidden border z-10 hover:brightness-95 transition-all ${c.bg} ${c.border} ${c.text}`}
                      style={{
                        top: top + 1,
                        height: Math.max(height - 2, 20),
                        left: `${col * colW + 1}%`,
                        width: `${colW - 2}%`,
                      }}
                    >
                      <div className="text-xs font-bold leading-tight truncate">{m.title}</div>
                      {height > 30 && (
                        <div className="text-xs opacity-90 leading-tight mt-0.5">
                          {fmtTime(new Date(m.start_time))} – {fmtTime(new Date(m.end_time))}
                        </div>
                      )}
                      {height > 50 && m.context?.display_label && (
                        <div className="text-xs opacity-80 truncate mt-0.5">{m.context.display_label}</div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default TimeGrid;
